'use client';

import { useEffect, useRef } from 'react';

interface UseInfiniteScrollProps {
  onLoadMore?: () => void;
  hasMore: boolean;
  isLoading: boolean;
  rootMargin?: string;
}

export function useInfiniteScroll({ onLoadMore, hasMore, isLoading, rootMargin = '100px' }: UseInfiniteScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!(onLoadMore && hasMore) || isLoading) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        root: containerRef.current,
        rootMargin,
        threshold: 0.1,
      },
    );

    const trigger = triggerRef.current;
    if (trigger) {
      observer.observe(trigger);
    }

    return () => {
      if (trigger) {
        observer.unobserve(trigger);
      }
      observer.disconnect();
    };
  }, [onLoadMore, hasMore, isLoading, rootMargin]);

  return {
    containerRef,
    triggerRef,
  };
}
